import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Info, RefreshCw, X } from "lucide-react";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import Tabs from "../../Components/Tabs";
import AccessBanner from "./AccessBanner";
import CurrentPlanCard from "./CurrentPlanCard";
import ChangePlanModal from "./ChangePlanModal";
import UsageCard from "./UsageCard";
import AddOnsCard from "./AddOnsCard";
import YearlyPromoCard from "./YearlyPromoCard";
import PaymentMethodCard from "./PaymentMethodCard";
import PaymentMethodsPanel from "./PaymentMethodsPanel";
import InvoicesPanel from "./InvoicesPanel";
import BillingSummaryCard from "./BillingSummaryCard";
import NeedHelpCard from "./NeedHelpCard";
import { verifyToken } from "../../actions/auth";
import { emitBillingBusy } from "../../utils/billingEvents";
import { getSubscription, listPlans } from "../../actions/billing";
import LoadingState from "../../Components/LoadingState";

const billingTabs = ["Subscription", "Invoices", "Payment methods"];

// ?tab=Invoices opens the page there. Written by the bell's billing
// notifications, and worth honouring from anywhere else that wants to link
// straight to a panel rather than to the page.
const tabFromParam = (value) =>
  billingTabs.find(
    (tab) => tab.toLowerCase() === String(value || "").toLowerCase(),
  ) || "";

// A payment is taken in a card popup over the page, so there is no return
// trip to read. The plan does not always arrive with it, though: Paddle
// activates from a signed webhook that lands after the browser is finished,
// and Helcim can answer "processing" for the same reason. So the record is
// read until it shows the change rather than once, and the page waits behind
// a loader meanwhile.
const SETTLE_POLL_MS = 3000;
const SETTLE_TIMEOUT_MS = 60000;

// Said after the reload below, since the page that would have shown it is
// gone by then. Session storage, because it belongs to this tab and this trip.
const PAID_NOTICE_KEY = "hostor:paidNotice";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// What "it arrived" looks like: a different plan, a different cycle, a new
// subscription behind it, or an account that is working again.
const hasChanged = (before, record) => {
  if (!record) return false;

  const idOf = (r) =>
    r?.helcimSubscriptionId || r?.paddleSubscriptionId || r?.subscriptionId || "";

  if (record.plan !== before.plan) return true;
  if (record.billingInterval !== before.interval) return true;
  if (idOf(record) && idOf(record) !== before.id) return true;

  return record.status === "active" && before.status !== "active";
};
export default function Billing() {
  const dispatch = useDispatch();
  const currentPlan = useSelector(
    (state) => state.auth.user?.subscription?.plan || "free",
  );

  const [params, setParams] = useSearchParams();
  const [outcome, setOutcome] = useState(null);

  const [activeTab, setActiveTab] = useState(
    () => tabFromParam(params.get("tab")) || "Subscription",
  );
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [enabled, setEnabled] = useState({ conversations: false, branding: false });

  // Fetched once here rather than in each card: the plan card and the payment
  // card read the same record, and two components fetching it separately
  // meant two requests for one screen.
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingLoading, setBillingLoading] = useState(true);
  // Set while a payment is being taken and confirmed in the modal. The record
  // the page has until then is the plan from *before* it, and showing that to
  // someone who has just paid for a different one reads as a failed purchase.
  const [confirming, setConfirming] = useState(false);

  const loadBilling = useCallback(async () => {
    const [current, tiers] = await Promise.all([
      dispatch(getSubscription()),
      dispatch(listPlans()),
    ]);

    setBillingLoading(false);

    if (current?.success) setSubscription(current.subscription);
    if (tiers?.success) setPlans(tiers.plans);

    // Whether it answered at all, as well as what it said: the poll below has
    // to tell "not applied yet" from "this request is not going to work".
    return { ok: Boolean(current?.success), record: current?.subscription || null };
  }, [dispatch]);

  // Reads the subscription until the webhook has been and gone.
  //
  // What counts as "been and gone" depends on what was bought. For an upgrade
  // the subscription is active before the payment and active after it, so
  // waiting for an active plan stops immediately and shows the old one - which
  // is why the page only caught up on a refresh. When the plan that was bought
  // is known, the wait is for that plan to be the one on the record.
  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  // The page was reloaded by a payment that went through; this is the only
  // thing that survived it.
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(PAID_NOTICE_KEY);
      if (!notice) return;

      sessionStorage.removeItem(PAID_NOTICE_KEY);
      setOutcome({ kind: "success", text: notice });
    } catch {
      // No storage, no notice. The plan on screen is still the new one.
    }
  }, []);

  // The page may already be open when the link is followed, in which case the
  // initial state above has long since been set.
  useEffect(() => {
    const wanted = tabFromParam(params.get("tab"));
    if (wanted) setActiveTab(wanted);
  }, [params]);

  // A plan that has ended, or an account paused behind an unpaid renewal, is
  // a plan in name only: nothing is being billed and nothing is being served.
  // Treated as "no current plan" so the picker offers every tier, including
  // the one it was on - which the API answers as `resubscribe`.
  const planLive =
    Boolean(subscription) &&
    subscription.status !== "ended" &&
    !subscription.access?.paused;

  // Reads the subscription until it shows what was just paid for, then tells
  // the session record and the sidebar to catch up. Gives up after a minute -
  // the payment is through either way, the page is just behind.
  const settleAfterPayment = useCallback(async (message) => {
    const before = {
      plan: subscription?.plan || "",
      interval: subscription?.billingInterval || "",
      id:
        subscription?.helcimSubscriptionId ||
        subscription?.paddleSubscriptionId ||
        "",
      status: subscription?.status || "",
    };

    setConfirming(true);
    emitBillingBusy(true);

    const deadline = Date.now() + SETTLE_TIMEOUT_MS;
    let arrived = false;

    while (!arrived && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      const read = await loadBilling();

      // A request that failed will keep failing; the signed-out handler has
      // already taken over by then.
      if (!read.ok) break;

      arrived = hasChanged(before, read.record);
      // eslint-disable-next-line no-await-in-loop
      if (!arrived) await wait(SETTLE_POLL_MS);
    }

    // The plan is in. Every card on this page, the sidebar, the usage meters
    // and the invoice list all read it separately, so the page is loaded again
    // rather than each of them being told to catch up.
    if (arrived) {
      try {
        sessionStorage.setItem(
          PAID_NOTICE_KEY,
          message || "Payment received. Your new plan is active.",
        );
      } catch {
        // A tab that refuses storage still reloads; it just says nothing.
      }

      window.location.reload();
      return;
    }

    await dispatch(verifyToken());
    setConfirming(false);
    emitBillingBusy(false);

    // A minute without it is a late webhook rather than a failed payment, so
    // the page says so instead of leaving the old plan on screen unexplained.
    setOutcome({
      kind: "pending",
      text: "Your payment is being processed. This page will update shortly.",
    });
  }, [subscription, loadBilling, dispatch]);

  const toggleAddOn = (id) => {
    setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar title="Billing" subtitle="Manage your subscription, payments and invoices." />

        <div className="dash-content">
          {/* Held back until the page has something to show it against:
              "your subscription is active" over a spinner, with the previous
              plan still underneath it, is a claim the page cannot back up
              yet. */}
          {outcome && !billingLoading && !confirming && (
            <div className={`bill-outcome is-${outcome.kind}`} role="status">
              <span className="bill-outcome__icon">
                {outcome.kind === "success" ? (
                  <CheckCircle2 size={18} strokeWidth={2} />
                ) : (
                  <Info size={18} strokeWidth={2} />
                )}
              </span>
              {outcome.text}

              {outcome.kind === "pending" && (
                <button
                  type="button"
                  className="bill-outcome__refresh"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw size={14} strokeWidth={2.2} />
                  Refresh
                </button>
              )}

              <button
                type="button"
                className="bill-outcome__close"
                onClick={() => setOutcome(null)}
                aria-label="Dismiss"
              >
                <X size={15} strokeWidth={2.2} />
              </button>
            </div>
          )}

          {/* Cancelled, past due, or paused - whichever the server says, with
              the step that gets out of it. Above the tabs, because it applies
              to the account rather than to one panel. */}
          <AccessBanner
            access={subscription?.access}
            onChoosePlan={() => setPlanModalOpen(true)}
            onRetried={settleAfterPayment}
          />

          <Tabs
            tabs={billingTabs}
            active={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);

              const next = new URLSearchParams(params);
              if (tab === "Subscription") next.delete("tab");
              else next.set("tab", tab);

              setParams(next, { replace: true });
            }}
          />

          {/* One spinner for the whole page until the subscription is in
              hand. The cards each have a skeleton of their own, but five of
              them resolving at different moments reads as a page that cannot
              make up its mind. */}
          {billingLoading || confirming ? (
            <LoadingState
              title={
                confirming
                  ? "Confirming your payment"
                  : "Loading your billing"
              }
              detail={
                confirming
                  ? "The card has gone through; the plan follows a moment later."
                  : undefined
              }
              inline
            />
          ) : (
            <>

          {activeTab === "Subscription" ? (
            <>
              <div className="bill-grid">
                <div className="cz-form-col">
                  <CurrentPlanCard
                    subscription={subscription}
                    plans={plans}
                    onChangePlan={() => setPlanModalOpen(true)}
                    onCancelled={() => {
                      emitBillingBusy(false);
                      // The plan, its limits and the sidebar all change at
                      // once, and the sidebar reads from the session record.
                      loadBilling();
                      dispatch(verifyToken());
                      setOutcome({
                        kind: "cancelled",
                        text: "Auto-renewal is off. Your plan keeps working until the end of the period you have paid for.",
                      });
                    }}
                  />
                  <UsageCard />
                  {/* <AddOnsCard enabled={enabled} onToggle={toggleAddOn} /> */}
                  <NeedHelpCard />
                </div>

                <div className="cz-form-col">
                  <PaymentMethodCard
                    subscription={subscription}
                    loading={billingLoading}
                    onChangePlan={() => setPlanModalOpen(true)}
                  />
                  <BillingSummaryCard subscription={subscription} plans={plans} />
                </div>
              </div>

              {/* <YearlyPromoCard onSwitch={() => {}} /> */}
            </>
          ) : activeTab === "Invoices" ? (
            <InvoicesPanel />
          ) : activeTab === "Payment methods" ? (
            <PaymentMethodsPanel
              subscription={subscription}
              loading={billingLoading}
              onChangePlan={() => setPlanModalOpen(true)}
            />
          ) : (
            <section className="dash-card onboarding__card--placeholder">
              <h2 className="onboarding__card-title">{activeTab} is coming soon</h2>
              <p className="onboarding__card-subtitle">
                We&apos;re still building this tab. Check back soon, or head back to Subscription.
              </p>
              <button
                type="button"
                className="onboarding__btn onboarding__btn--primary"
                onClick={() => setActiveTab("Subscription")}
              >
                Back to Subscription
              </button>
            </section>
          )}
            </>
          )}
        </div>
      </div>

      {/* currentInterval: the cycle the plan is billed for, so the same tier
          on the other cycle is offered as a change rather than shown as the
          plan the account is already on.

          planActive: false once the plan has ended or the account is paused,
          when buying the same plan again is the whole point and no card should
          be sitting there disabled as "your plan". */}
      <ChangePlanModal
        open={planModalOpen}
        currentPlan={currentPlan}
        currentInterval={subscription?.billingInterval || ""}
        planActive={planLive}
        onClose={(changed, message) => {
          setPlanModalOpen(false);
          // A plan applied server-side needs /auth/me re-read for the card and
          // the sidebar to show the new allowances.
          if (!changed) return;

          // The plan is often a moment behind the money - always so with
          // Paddle, which activates from a webhook - so this waits for the
          // record to show it, then loads the page again on top of it. The
          // backend's own sentence is carried across the reload.
          settleAfterPayment(message);
        }}
      />
    </div>
  );
}
